import { useCallback, useMemo } from 'react';
import { CONTINENT_LIST } from '../../../shared/utils/regions';
import { getEnabledPlanes } from '../../../shared/utils/products';

const countryRegionsByContinent = (productRegions) => {
    const tmpMap = {};
    if (productRegions?.length) {
        productRegions.forEach(region => {
            const { continent, country } = region;
            if (!continent || !country) return;
            if (!tmpMap[continent]) tmpMap[continent] = {};
            if (!tmpMap[continent][country]) tmpMap[continent][country] = [];
            tmpMap[continent][country].push(region);
        });
    }
    return tmpMap;
};

// Walks a leaf-only TreeSelect item tree (continent -> country -> city) and returns
// every leaf `city-*` value. Used both for the region tree's "select all" behavior and
// for pre-selecting every region when a product is newly added.
const collectLeafValues = (items) => {
    const leaves = [];
    const recurse = (nodes) => {
        for (const node of nodes) {
            if (node.children?.length) recurse(node.children);
            else leaves.push(node.value);
        }
    };
    recurse(items || []);
    return leaves;
};

/**
 * Builds and caches the continent -> country -> city TreeSelect item trees used by the
 * region pickers in ManageSubscriptionsModal (and, via `getLeafValues`, the "Add Product"
 * pre-selection).
 *
 * Building this tree walks every product's location list, which is expensive to redo on
 * every render (e.g. once per chip removal). TreeSelect/TreeList also memoize
 * parent/checked state internally keyed on `items` identity, so a fresh array on every
 * render would silently invalidate those memos too. The cache is keyed by product type
 * for the single-plane "combined" tree, and by `${product_type}::${planeType}` for the
 * per-plane trees multi-plane products use.
 */
export const useRegionTreeItems = (products) => {
    const regionsFilterItems = useCallback((productType, locations) => {
        const tmpItems = [];
        const productData = products.find(p => p.product_type === productType);
        const regionsList = locations
            || [
                ...(productData?.planes?.control?.locations || []),
                ...(productData?.planes?.data?.locations || []),
            ];

        CONTINENT_LIST.slice().forEach(continentName => {
            const continentFilterItem = {
                value: `continent-${continentName}`,
                label: continentName,
                type: 'continent',
                children: [],
            };
            const grouped = countryRegionsByContinent(regionsList);
            const countryRegions = grouped?.[continentName];

            if (!countryRegions) return;

            const sortedCountryNames = Object.keys(countryRegions).sort((a, b) => a.localeCompare(b));
            const usaNdx = sortedCountryNames.indexOf('USA');
            if (usaNdx > -1) {
                sortedCountryNames.splice(usaNdx, 1);
                sortedCountryNames.unshift('USA');
            }

            sortedCountryNames.forEach(countryName => {
                const countryFilterItem = {
                    value: `country-${countryName}`,
                    label: countryName,
                    type: 'country',
                    children: [],
                };
                const tmpRegions = countryRegions?.[countryName] || [];
                const citiesMap = {};
                tmpRegions.forEach(r => { citiesMap[r.city] = 1; });

                Object.keys(citiesMap).sort((a, b) => a.localeCompare(b)).forEach(cityName => {
                    countryFilterItem.children.push({
                        value: `city-${cityName}`,
                        label: cityName,
                        type: 'city',
                    });
                });

                continentFilterItem.children.push(countryFilterItem);
            });

            tmpItems.push(continentFilterItem);
        });
        return tmpItems;
    }, [products]);

    const regionItemsCache = useMemo(() => {
        const cache = new Map();
        products.forEach(p => {
            const enabledPlanes = getEnabledPlanes(p);
            const multiPlane = enabledPlanes.length >= 2;
            cache.set(p.product_type, regionsFilterItems(p.product_type));
            if (multiPlane) {
                enabledPlanes.forEach(([planeType, planeData]) => {
                    cache.set(`${p.product_type}::${planeType}`, regionsFilterItems(p.product_type, planeData.locations));
                });
            }
        });
        return cache;
    }, [products, regionsFilterItems]);

    const getRegionItems = useCallback((productType, planeType) => {
        const key = planeType ? `${productType}::${planeType}` : productType;
        return regionItemsCache.get(key) || [];
    }, [regionItemsCache]);

    // Every leaf `city-*` value for a product (or one of its planes), used to
    // pre-select all regions when a product is newly added via Add Product.
    const getLeafValues = useCallback((productType, planeType) => (
        collectLeafValues(getRegionItems(productType, planeType))
    ), [getRegionItems]);

    return { getRegionItems, getLeafValues };
};

export default useRegionTreeItems;
